import React from 'react';
import { Link } from 'react-router-dom';
import './GameSummary.css';
import {
  buildUserIdByEmail,
  formatSummaryName,
  normalizeEmail,
  resolveTopScorersFromTeams,
} from '../../utils/gameSummary';
import { buildPlayerPath, buildProfilePath, isOwnPlayerPath } from '../../utils/appRoutes';

/** Your own name goes to your profile; everyone else's to their public one. */
function buildPlayerLink(userId, currentUserId) {
  const normalizedId = String(userId || '').trim();
  if (!normalizedId) return null;
  return isOwnPlayerPath(normalizedId, currentUserId)
    ? buildProfilePath()
    : buildPlayerPath(normalizedId);
}

/** A name that links to a profile when the player has an account. */
function PlayerName({ children, userId, currentUserId, className }) {
  const to = buildPlayerLink(userId, currentUserId);

  if (!to) {
    return <span className={className}>{children}</span>;
  }

  return (
    <Link to={to} className={`${className} game-summary-player-link`}>
      {children}
    </Link>
  );
}

const PERIODS = ['1st Half', '2nd Half'];

function sortMatchEvents(matchEvents) {
  return [...(Array.isArray(matchEvents) ? matchEvents : [])].sort((left, right) => {
    if ((left?.minute || 0) !== (right?.minute || 0)) {
      return (left?.minute || 0) - (right?.minute || 0);
    }

    return (left?.sortIndex || 0) - (right?.sortIndex || 0);
  });
}

function annotateGoals(matchEvents) {
  const score = [0, 0];

  return sortMatchEvents(matchEvents).map((event) => {
    if (event?.type === 'goal' && (event.teamIndex === 0 || event.teamIndex === 1)) {
      score[event.teamIndex] += 1;
      return {
        ...event,
        runningScore: `${score[0]} - ${score[1]}`,
      };
    }

    return {
      ...event,
      runningScore: '',
    };
  });
}

function getGoalTotals(matchEvents) {
  const score = [0, 0];

  (Array.isArray(matchEvents) ? matchEvents : []).forEach((event) => {
    if (event?.type !== 'goal') return;
    const teamIndex = Number(event?.teamIndex);
    if (teamIndex === 0 || teamIndex === 1) {
      score[teamIndex] += 1;
    }
  });

  return score;
}

function getEventSide(event) {
  const teamIndex = event?.type === 'team_switch' ? event?.toTeamIndex : event?.teamIndex;
  return teamIndex === 1 ? 'right' : 'left';
}

function getEventPlayerName(event) {
  const formattedName = formatSummaryName(event?.playerName);
  if (formattedName) return formattedName;

  return event?.playerEmail || 'Unknown player';
}

function formatTeamNumber(teamIndex) {
  const normalized = Number(teamIndex);
  return Number.isInteger(normalized) ? normalized + 1 : '?';
}

function getPeriodScore(matchEvents) {
  const score = [0, 0];

  (Array.isArray(matchEvents) ? matchEvents : []).forEach((event) => {
    if (event?.type === 'goal' && (event.teamIndex === 0 || event.teamIndex === 1)) {
      score[event.teamIndex] += 1;
    }
  });

  return `${score[0]} - ${score[1]}`;
}

function GoalIcon() {
  return (
    <span className="game-summary-icon game-summary-icon--goal" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <circle cx="12" cy="12" r="8.75" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path
          d="M12 7.1L14.9 9.1L13.8 12.4H10.2L9.1 9.1L12 7.1Z"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
        <path
          d="M9.1 9.1L6.4 8.3L4.9 10.9L5.9 13.9L8.8 14.6L10.2 12.4M14.9 9.1L17.6 8.3L19.1 10.9L18.1 13.9L15.2 14.6L13.8 12.4M8.8 14.6L10.3 17.2H13.7L15.2 14.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.15"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function SwitchIcon() {
  return (
    <span className="game-summary-icon game-summary-icon--switch" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <path
          d="M7 9h7l-1.8-1.8M14 9l-1.8 1.8"
          fill="none"
          stroke="#ff4b4b"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M17 15h-7l1.8-1.8M10 15l1.8 1.8"
          fill="none"
          stroke="#00ff00"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function YellowCardIcon() {
  return (
    <span className="game-summary-icon game-summary-icon--yellow" aria-hidden="true">
      <svg viewBox="0 0 16 20" focusable="false">
        <rect x="3" y="2" width="10" height="16" rx="2" fill="currentColor" />
      </svg>
    </span>
  );
}

function RedCardIcon() {
  return (
    <span className="game-summary-icon game-summary-icon--red" aria-hidden="true">
      <svg viewBox="0 0 16 20" focusable="false">
        <rect x="3" y="2" width="10" height="16" rx="2" fill="currentColor" />
      </svg>
    </span>
  );
}

function renderGoalEvent(event, side, ctx) {
  const player = (
    <PlayerName
      userId={ctx?.userIdByEmail?.get(normalizeEmail(event?.playerEmail))}
      currentUserId={ctx?.currentUserId}
      className="game-summary-event-player"
    >
      {getEventPlayerName(event)}
    </PlayerName>
  );
  const formattedAssistName = formatSummaryName(event?.assistName);
  const assist = event?.assistName ? (
    <span className="game-summary-event-assist">({formattedAssistName || event.assistName})</span>
  ) : null;
  const score = event?.runningScore ? (
    <span className="game-summary-event-score">{event.runningScore}</span>
  ) : null;

  if (side === 'right') {
    return (
      <>
        {assist}
        {player}
        {score}
        <GoalIcon />
      </>
    );
  }

  return (
    <>
      <GoalIcon />
      {score}
      {player}
      {assist}
    </>
  );
}

function renderCardEvent(event, side, ctx) {
  const player = (
    <PlayerName
      userId={ctx?.userIdByEmail?.get(normalizeEmail(event?.playerEmail))}
      currentUserId={ctx?.currentUserId}
      className="game-summary-event-player"
    >
      {getEventPlayerName(event)}
    </PlayerName>
  );
  const icon = event?.type === 'red_card' ? <RedCardIcon /> : <YellowCardIcon />;

  if (side === 'right') {
    return (
      <>
        {player}
        {icon}
      </>
    );
  }

  return (
    <>
      {icon}
      {player}
    </>
  );
}

function renderSwitchEvent(event, side, ctx) {
  const player = (
    <PlayerName
      userId={ctx?.userIdByEmail?.get(normalizeEmail(event?.playerEmail))}
      currentUserId={ctx?.currentUserId}
      className="game-summary-event-player"
    >
      {getEventPlayerName(event)}
    </PlayerName>
  );
  const detail = (
    <span className="game-summary-event-detail">
      T{formatTeamNumber(event?.fromTeamIndex)} -> T{formatTeamNumber(event?.toTeamIndex)}
    </span>
  );

  if (side === 'right') {
    return (
      <>
        {detail}
        {player}
        <SwitchIcon />
      </>
    );
  }

  return (
    <>
      <SwitchIcon />
      {player}
      {detail}
    </>
  );
}

function renderEventContent(event, side, ctx) {
  if (event?.type === 'goal') {
    return renderGoalEvent(event, side, ctx);
  }

  if (event?.type === 'team_switch') {
    return renderSwitchEvent(event, side, ctx);
  }

  if (event?.type === 'yellow_card' || event?.type === 'red_card') {
    return renderCardEvent(event, side, ctx);
  }

  return (
    <PlayerName
      userId={ctx?.userIdByEmail?.get(normalizeEmail(event?.playerEmail))}
      currentUserId={ctx?.currentUserId}
      className="game-summary-event-player"
    >
      {getEventPlayerName(event)}
    </PlayerName>
  );
}

function renderEventRow(event, ctx) {
  const side = getEventSide(event);

  return (
    <div
      key={`${event.sortIndex}-${event.type}-${event.playerEmail || event.playerName}`}
      className={`game-summary-row game-summary-row--${side}`}
    >
      <div className={`game-summary-event game-summary-event--${side}`}>
        {side === 'left' ? (
          <>
            <span className="game-summary-minute">{event.minute}'</span>
            {renderEventContent(event, side, ctx)}
          </>
        ) : (
          <>
            {renderEventContent(event, side, ctx)}
            <span className="game-summary-minute">{event.minute}'</span>
          </>
        )}
      </div>
    </div>
  );
}

export default function GameSummary({
  result,
  matchEvents,
  teams = [[], []],
  userResult,
  timeLabel = '',
  dateLabel = '',
  gameName = '',
  tournament = '',
  gameStatus = '',
  currentUserId = null,
}) {
  const events = annotateGoals(matchEvents);
  // Events name a player by email; the team entries carry the account id.
  const linkContext = { userIdByEmail: buildUserIdByEmail(teams), currentUserId };
  const storedTeam1Goals = Number(result?.team1Goals) || 0;
  const storedTeam2Goals = Number(result?.team2Goals) || 0;
  const [eventTeam1Goals, eventTeam2Goals] = getGoalTotals(events);
  const hasScoreMismatch =
    events.length > 0 &&
    (eventTeam1Goals !== storedTeam1Goals || eventTeam2Goals !== storedTeam2Goals);
  const team1Goals = hasScoreMismatch ? eventTeam1Goals : storedTeam1Goals;
  const team2Goals = hasScoreMismatch ? eventTeam2Goals : storedTeam2Goals;
  const normalizedStatus = String(gameStatus || '').trim().toLowerCase();
  const statusLabel = normalizedStatus === 'finished' ? 'Finished' : 'Final';
  const kickoffLabel = [timeLabel, dateLabel].filter(Boolean).join('  ');
  const summaryClassName = events.length > 0 ? 'game-summary game-summary--with-timeline' : 'game-summary';
  const { topScorers, goalCount: topScorerGoalCount } = resolveTopScorersFromTeams(teams);
  const topScorerLabel = topScorers.length > 1 ? 'Top Scorers' : 'Top Scorer';
  const topScorerGoalsLabel =
    topScorerGoalCount === 1 ? '1 goal' : `${topScorerGoalCount} goals`;

  return (
    <div className={summaryClassName}>
      {(kickoffLabel || gameName || tournament) ? (
        <div className="game-summary-match-head">
          {kickoffLabel ? <div className="game-summary-kickoff">{kickoffLabel}</div> : null}
          {gameName ? <div className="game-summary-match-name">{gameName}</div> : null}
          {tournament ? <div className="game-summary-match-type">{tournament}</div> : null}
        </div>
      ) : null}
      <div className="game-summary-scoreboard">
        <span className="game-summary-team">Team 1</span>
        <div className="game-summary-score">
          <span>{team1Goals}</span>
          <span>-</span>
          <span>{team2Goals}</span>
        </div>
        <span className="game-summary-team">Team 2</span>
      </div>
      <div className="game-summary-status">{statusLabel}</div>
      {userResult ? <div className="game-summary-outcome">{userResult}</div> : null}
      {topScorers.length > 0 ? (
        <div className="game-summary-top-scorers">
          <div className="game-summary-top-scorers-label">{topScorerLabel}</div>
          <div className="game-summary-top-scorers-names">
            {topScorers.map((scorer, index) => (
              <React.Fragment key={`${scorer.name}-${index}`}>
                {index > 0 ? ', ' : ''}
                <PlayerName
                  userId={scorer.userId}
                  currentUserId={currentUserId}
                  className="game-summary-top-scorer-name"
                >
                  {scorer.name}
                </PlayerName>
              </React.Fragment>
            ))}
          </div>
          <div className="game-summary-top-scorers-goals">
            {topScorerGoalsLabel}
            {topScorers.length > 1 ? ' each' : ''}
          </div>
        </div>
      ) : null}

      {events.length === 0 ? (
        <div className="game-summary-empty">
          Detailed match summary is not available for this game yet.
        </div>
      ) : (
        <div className="game-summary-timeline">
          {PERIODS.map((period) => {
            const periodEvents = events.filter((event) => event?.period === period);

            return (
              <section key={period} className="game-summary-period">
                <div className="game-summary-period-header">
                  <span>{period}</span>
                  <span className="game-summary-period-score">{getPeriodScore(periodEvents)}</span>
                </div>
                {periodEvents.length > 0 ? (
                  <div className="game-summary-period-body">
                    {periodEvents.map((event) => renderEventRow(event, linkContext))}
                  </div>
                ) : (
                  <div className="game-summary-period-empty">No events recorded.</div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
